import { Category } from '@wix/answers-api/dist/kb';
import {
  Box,
  Divider,
  Heading,
  Text,
} from '@wix/design-system';
import { ArticleSearchResult, ArticleSearchResultItem } from '../../../types';
import { FC, useContext } from 'react';
import {
  EXPERIMENTS,
} from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { SearchResultItem } from '../SearchResultItem';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { useSearchResultsBi } from '../bi';
import { Context } from '../../../context';
import { useRouter } from 'next/router';
import css from '../index.module.scss';

type SearchResultItemsProps = {
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  term: string;
  displayGeneratedAnswer: boolean;
};

export const SearchResultItems: FC<SearchResultItemsProps> = ({
  categoriesPaths,
  searchResults,
  term,
  displayGeneratedAnswer,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const context = useContext(Context);
  const { searchSessionId, pageType } = context;
  const { experiments } = useExperiments({ readOnly: true });
  const biEvents = useSearchResultsBi(term, locale as string, searchSessionId);

  const items = searchResults.items;

  const onArticleResolutionBadgeClick = (clickedText: string, url: string, itemOrder: number) => {
      biEvents.onArticleResolutionBadgeClick(clickedText, url, itemOrder);
    };

  const onArticleTypeTagClick = (clickedText: string, url: string, itemOrder: number) => {
    biEvents.onArticleTypeTagClick(clickedText, url, itemOrder);
  };

  const onArticleTopicBadgeClick = (topic: Category, itemOrder: number) => {
    biEvents.onArticleTopicBadgeClick(topic, itemOrder);
  };

  const onArticleClick = (article: ArticleSearchResultItem, itemOrder: number) => {
    biEvents.onArticleClick(article, itemOrder, pageType, searchResults.searchMethod);
  };

  const getSearchResultsItems = () => items.map((searchResult, idx) => {
    const rootTopic = searchResult.categoryId && categoriesPaths?.[searchResult.categoryId]?.[0];
    const itemOrder = idx + 1;
    return (
      <>
      {experiments.enabled(EXPERIMENTS.SPECS.SHOW_DISTANCE_FOR_RAM)
        && <Text weight="bold">Distance: {searchResult.distance}</Text>}
      <SearchResultItem
        key={searchResult.id}
        rootTopic={rootTopic || undefined}
        onArticleResolutionBadgeClick={
          (clickedText: string, url: string) => onArticleResolutionBadgeClick(clickedText, url, itemOrder)
        }
        onArticleTypeTagClick={
          (clickedText: string, url: string) => onArticleTypeTagClick(clickedText, url, itemOrder)
        }
        onArticleTopicBadgeClick={(topic) => onArticleTopicBadgeClick(topic, itemOrder)}
        onArticleClick={() => onArticleClick(searchResult, itemOrder)}
        {...searchResult}
        isGeneratedAnswerDisplayed={displayGeneratedAnswer}
        docType={searchResult.docType}
      />
      {idx < items.length - 1 ? <Divider /> : null}
    </>
    );
  });

  const getEmptySearchResults = () => (
    <Box
      direction="vertical"
      align="center"
      className={css.noResultsForFilter}
      dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOR_FILTER}
    >
      <Heading appearance="H3" className={css.noResultsForFilterTitle}>
        {t('search-results-page.no-results-found-under-filter.title', { term })}
      </Heading>
      <Text size="medium" className={css.noResultsForFilterDescription}>
        {t('search-results-page.no-results-found-under-filter.description')}
      </Text>
    </Box>
  );


  return (
    <Box direction="vertical" className={css.searchResultsList}>
      {items.length ? getSearchResultsItems() : getEmptySearchResults()}
    </Box>
  );
};
